import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { Link } from "react-router-dom";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { statusHex, statusClasses } from "../data/statusColors";
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  Droplets, Flame, Wind, Shield, Users, Truck,
  MapPin, Clock,
} from "lucide-react";
import { useTranslate } from "../i18n/useTranslate";

// ── inline data ──────────────────────────────────────────────────
const NDRF_PLATOONS = [
  { id:"NDRF-P1", name:"NDRF 4th Bn — Platoon Alpha",  zone:"Ramkund Ghat",     strength:45, available:45, deployed:0,  task:"Crowd support standby", status:"Standby",  readiness:100 },
  { id:"NDRF-P2", name:"NDRF 4th Bn — Platoon Bravo",  zone:"Camp 3 / OFZ-B",   strength:45, available:38, deployed:7,  task:"Shelter overflow mgmt", status:"Active",   readiness:84  },
  { id:"NDRF-P3", name:"NDRF 4th Bn — Platoon Charlie",zone:"Gangapur Dam area", strength:45, available:45, deployed:0,  task:"Flood pre-position",    status:"Standby",  readiness:100 },
  { id:"NDRF-P4", name:"NDRF 4th Bn — Platoon Delta",  zone:"Trimbakeshwar",     strength:45, available:42, deployed:3,  task:"River bank patrol",     status:"Active",   readiness:93  },
  { id:"NDRF-MP1",name:"Maharashtra SDRF — Team 1",    zone:"EOC Reserve",       strength:30, available:30, deployed:0,  task:"EOC standby",           status:"Standby",  readiness:100 },
  { id:"NDRF-MP2",name:"Maharashtra SDRF — Team 2",    zone:"Panchvati",         strength:30, available:28, deployed:2,  task:"Crowd assistance",      status:"Active",   readiness:93  },
];

const FIRE_STATIONS = [
  { station:"Nashik City FS",         zone:"Nashik Urban",   engines:4, personnel:22, available:3, tankers:2, status:"Active", lastCall:"13:44", responseTime:"4.2 min" },
  { station:"Panchvati Fire Post",    zone:"Panchvati",      engines:2, personnel:12, available:2, tankers:1, status:"Active", lastCall:"11:20", responseTime:"2.8 min" },
  { station:"Trimbakeshwar FS",       zone:"Trimbakeshwar",  engines:3, personnel:18, available:3, tankers:2, status:"Active", lastCall:"08:30", responseTime:"5.1 min" },
  { station:"Nashik Road Post",       zone:"Nashik Road",    engines:2, personnel:10, available:1, tankers:1, status:"Alert",  lastCall:"14:10", responseTime:"3.4 min" },
  { station:"Gangapur Mobile Unit",   zone:"Gangapur Camp",  engines:1, personnel:8,  available:1, tankers:1, status:"Active", lastCall:"12:00", responseTime:"6.2 min" },
];

const SAR_TEAMS = [
  { team:"SAR Alpha — Water Rescue",  members:12, zone:"Godavari Ghat",    equipment:"2 boats · life rings · scuba",  status:"Deployed",  lastAction:"14:00" },
  { team:"SAR Bravo — Urban SAR",     members:10, zone:"Ramkund Entry 2",  equipment:"Stretchers · harness · breacher",status:"Active",    lastAction:"13:44" },
  { team:"SAR Charlie — Medical SAR", members:8,  zone:"Camp Beta",        equipment:"ALS kit · oxygen · defibrillator",status:"Standby",  lastAction:"12:30" },
  { team:"SAR Delta — Canine Unit",   members:4,  zone:"Missing person zones",equipment:"2 trained dogs · GPS tracker",  status:"Active",  lastAction:"14:12" },
];

const DAM_DATA = [
  { time:"06:00", level:82, discharge:180, inflow:160 },
  { time:"08:00", level:83, discharge:196, inflow:180 },
  { time:"10:00", level:85, discharge:210, inflow:200 },
  { time:"12:00", level:86, discharge:228, inflow:220 },
  { time:"14:00", level:87, discharge:244, inflow:240 },
  { time:"16:00", level:89, discharge:272, inflow:265 },
  { time:"18:00", level:91, discharge:310, inflow:300 },
];

const DISASTER_SCENARIOS = [
  {
    id:"DS-FLOOD", name:"Godavari Flash Flood",       probability:14, severity:"HIGH",
    trigger:"Dam level >90% + discharge >350 cumecs + rainfall >20mm/hr",
    steps:["Issue evacuation advisory — all Ghats","Activate NDRF Platoon Charlie","Close Godavari Bridge to pilgrims","Open OFZ-A overflow shelter","Deploy 2 rescue boats"],
    resources:["NDRF-P3 (45 personnel)","2 rescue boats armed","4 overflow shelters ready","Police cordoning team"],
    evacuationRoute:"Godavari Ghat → High Ground Via Camp Alpha",
  },
  {
    id:"DS-FIRE",  name:"Mass Casualty Fire Event",   probability:8,  severity:"HIGH",
    trigger:"Fire at camp / tent area with >50 persons",
    steps:["Alert nearest fire station","Evacuate 100m radius","NDRF SAR team deploy","Activate 3 ambulances","Establish command post"],
    resources:["3 fire engines within 5 min","NDRF SAR Alpha","4 ambulances","Medical emergency team"],
    evacuationRoute:"Camp → Designated Assembly Point Zone D",
  },
  {
    id:"DS-STRUCT","name":"Bridge / Structure Failure",probability:4,  severity:"CRITICAL",
    trigger:"Godavari Bridge structural alarm OR load >95% + vehicle overrun",
    steps:["Immediate closure — all traffic","Evacuate bridge vicinity 200m","NDRF urban SAR deploy","Police cordon + divert","Structural engineer on-site within 30 min"],
    resources:["NDRF SAR Bravo","Police 3 platoons","Civil engineering team","2 cranes pre-positioned"],
    evacuationRoute:"Divert via Nashik Road Flyover + Gangapur Causeway",
  },
];

const PRE_POSITIONED = [
  { resource:"Rescue Boats",         quantity:4,   location:"Godavari Ghat — Zone A",    status:"Ready"   },
  { resource:"Life Rings & Lines",   quantity:120, location:"All 5 Ghats",               status:"Ready"   },
  { resource:"Emergency Shelters",   quantity:6,   location:"OFZ-A/B/C + 3 school camps",status:"Ready"   },
  { resource:"Portable Pumps",       quantity:8,   location:"Flood-prone zones",          status:"Ready"   },
  { resource:"Generators (15 kVA)",  quantity:12,  location:"All camp perimeters",        status:"Armed"   },
  { resource:"Stretcher Teams",      quantity:24,  location:"Medical posts + ghats",      status:"Deployed"},
  { resource:"Emergency Food Kits",  quantity:5000,location:"Camp Alpha / Beta / Delta",  status:"Stocked" },
  { resource:"Inflatable Boats",     quantity:6,   location:"NDRF Platoon stores",        status:"Ready"   },
  { resource:"Body Bags",            quantity:200, location:"Civil Hospital + EOC",       status:"Sealed"  },
  { resource:"Chlorine Tablets",     quantity:50000,location:"All water points",          status:"Stocked" },
];

const IMD_ALERTS = [
  { alert:"Yellow Warning — Heavy Rain Nashik",    valid:"16:00–22:00, 9 Jun", severity:"YELLOW", issued:"IMD Pune 12:00" },
  { alert:"Orange Watch — Flooding Godavari",      valid:"18:00–02:00, 10 Jun",severity:"ORANGE", issued:"CWC Nashik 13:00"},
  { alert:"Heat Advisory — Nashik District",       valid:"All Day 9 Jun",      severity:"YELLOW", issued:"IMD Pune 06:00" },
  { alert:"Wind Speed Warning — Trimbakeshwar",    valid:"17:00–21:00, 9 Jun", severity:"YELLOW", issued:"IMD Pune 11:00" },
];

const SEV_BUCKET = { RED:"critical", ORANGE:"serious", YELLOW:"warning" };
const sevColor = s => statusClasses(SEV_BUCKET[s] || "info");

const ndStColor = s =>
  s === "Active"  ? "badge badge-info"    :
  s === "Standby" ? "badge badge-warning" :
  s === "Deployed"? "badge badge-critical": "badge badge-success";

const PREPOS_STATUS_BUCKET = { Ready:"good", Armed:"warning", Deployed:"info", Stocked:"neutral" };

// ── component ────────────────────────────────────────────────────
export default function DisasterManagement() {
  const [expandedScn, setExpandedScn] = useState(null);
  const t = useTranslate();

  // Computed from NDRF_PLATOONS so the KPI/banner can't drift from the
  // deployment board beneath them (previously hardcoded "2 Active" even
  // though 3 of the 6 platoons below are actually status "Active").
  const ndrfActiveCount  = NDRF_PLATOONS.filter(p => p.status === "Active").length;
  const ndrfStandbyCount = NDRF_PLATOONS.length - ndrfActiveCount;

  return (
    <div className="space-y-8">
      <SectionHeader icon="Shield" title="Disaster Management Command"
        subtitle="NDRF & SDRF deployment · fire stations · search & rescue teams · Gangapur Dam monitoring · flood preparedness · pre-positioned resources · IMD weather alerts · disaster scenario SOPs — Maha AI · Simhastha 2027"
        badge="NDRF LIVE"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="NDRF Platoons"   value="6 / 6"   icon="Shield"        color="blue"   index={0} live />
        <MetricCard title="Deployed"        value={`${ndrfActiveCount} Active`} icon="Users"         color="amber"  index={1} live />
        <MetricCard title="Fire Stations"   value="5 Online" icon="Flame"         color="red"    index={2} />
        <MetricCard title="SAR Teams"       value="4 Live"   icon="Activity"      color="green"  index={3} />
        <MetricCard title="Dam Level"       value="87%"      icon="Droplets"      color="amber"  index={4} live />
        <MetricCard title="Discharge"       value="244 cumecs" icon="TrendingUp"  color="blue"   index={5} live />
        <MetricCard title="Pre-Pos. Items"  value="10 Types" icon="Package"       color="green"  index={6} />
        <MetricCard title="IMD Alerts"      value="4 Active" icon="AlertTriangle" color="amber"  index={7} />
      </div>

      {/* Disaster Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-red-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">
                {t("MAHA AI — DISASTER MANAGEMENT COMMAND · NDRF 4TH BN OPERATIONAL")} — 14:35 IST
              </span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Disaster Management — Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("NDRF: 6 platoons/teams (2 active, 4 standby) · Fire: 5 stations, 12 engines · SAR: 4 teams deployed · Dam: 87% @ 244 cumecs ↑ · IMD: 1 Orange Watch · 3 scenarios pre-planned")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { label:"NDRF",   val:"6 Ready", ok:true  },
              { label:"Fire",   val:"5 Live",  ok:true  },
              { label:"SAR",    val:"4 Active",ok:true  },
              { label:"Dam",    val:"87%",     ok:false },
              { label:"Shelters",val:"6 Ready",ok:true  },
            ].map((s, i) => {
              const c = googleColor(i);
              return (
                <div key={s.label}
                  className="rounded-xl px-2.5 py-2 text-center min-w-[64px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-[8px] mb-0.5 font-black uppercase tracking-wide" style={{ color:c.text }}>{t(s.label)}</div>
                  <div className="text-[10px] font-black" style={{ color:c.text }}>{t(s.val)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* IMD Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {IMD_ALERTS.map((a, i) => (
          <motion.div key={a.alert}
            initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
            className={`p-4 rounded-2xl border ${sevColor(a.severity)}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className={a.severity === "ORANGE" ? "text-orange-600" : "text-amber-600"} />
              <div>
                <p className="text-xs font-black">{t(a.alert)}</p>
                <p className="text-[9px] mt-1 opacity-80">{t("Valid")}: {a.valid}</p>
                <p className="text-[8px] mt-0.5 opacity-60">{a.issued}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* NDRF + SAR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="NDRF & SDRF Deployment Board"
          subtitle="Platoon strength · zone · current task · readiness %">
          <div className="space-y-2">
            {NDRF_PLATOONS.map((p, i) => (
              <motion.div key={p.id}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className={`p-3 rounded-xl border ${p.status === "Active" ? "border-blue-200 bg-blue-50/10" : "border-slate-200"}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="font-mono text-[8px] text-slate-400">{p.id}</span>
                      <span className={ndStColor(p.status)}>{t(p.status)}</span>
                    </div>
                    <p className="text-xs font-black text-slate-900">{t(p.name)}</p>
                    <div className="flex items-center gap-1.5 text-[9px] text-slate-500 mt-0.5 flex-wrap">
                      <MapPin size={9} />{t(p.zone)}
                      <span>· {t(p.task)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className="h-1.5 rounded-full bg-blue-600"
                          initial={{ width:0 }} animate={{ width:`${p.readiness}%` }} transition={{ duration:0.8 }} />
                      </div>
                      <span className="text-[9px] font-black text-blue-700">{p.readiness}%</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-black text-blue-700">{p.available}/{p.strength}</div>
                    <div className="text-[9px] text-slate-500">{t("available")}</div>
                    {p.deployed > 0 && <div className="text-[9px] text-amber-600 font-bold">{p.deployed} {t("deployed")}</div>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Search & Rescue Teams"
          subtitle="4 specialised SAR teams · zone · equipment · status">
          <div className="space-y-3">
            {SAR_TEAMS.map((s, i) => (
              <motion.div key={s.team}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-3 rounded-xl border ${s.status === "Active" || s.status === "Deployed" ? "border-amber-200 bg-amber-50/20" : "border-slate-200"}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg gov-gradient flex items-center justify-center flex-shrink-0">
                    <Users size={14} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-black text-slate-900">{t(s.team)}</span>
                      <span className={ndStColor(s.status)}>{t(s.status)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-slate-500 mt-0.5 flex-wrap">
                      <MapPin size={9} />{t(s.zone)}
                      <span>· {s.members} {t("members")}</span>
                    </div>
                    <p className="text-[9px] text-blue-700 mt-1">{t(s.equipment)}</p>
                    <div className="text-[9px] text-slate-400 flex items-center gap-1 mt-0.5">
                      <Clock size={8} />{t("Last action")}: {s.lastAction}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            <ChartCard title="Fire Stations — Nashik & Trimbakeshwar"
              subtitle="5 stations · engines · personnel · response time">
              <div className="space-y-1.5">
                {FIRE_STATIONS.map((f, i) => (
                  <motion.div key={f.station}
                    initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}
                    className={`flex items-center gap-2 p-2 rounded-lg border text-xs flex-wrap ${
                      f.status === "Alert" ? "border-red-200 bg-red-50/20" : "border-slate-200"
                    }`}>
                    <Flame size={12} className="text-orange-500 flex-shrink-0" />
                    <div className="flex-1 min-w-[100px]">
                      <div className="font-black text-slate-900 text-[10px]">{t(f.station)}</div>
                      <div className="text-[8px] text-slate-500">
                        <Link to="/crowd-intelligence" className="hover:text-blue-700 hover:underline">{t(f.zone)}</Link> · {f.engines} {t("engines")} · {f.personnel} {t("staff")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] flex-shrink-0">
                      <span className="text-emerald-700 font-bold">{f.responseTime}</span>
                      <span className={f.status === "Alert" ? "badge badge-critical text-[8px]" : "badge badge-success text-[8px]"}>{t(f.status)}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </ChartCard>
          </div>
        </ChartCard>
      </div>

      {/* Dam Monitoring */}
      <ChartCard title="Gangapur Dam — Level · Discharge · Inflow Forecast"
        subtitle="Real-time dam level (%) · discharge (cumecs) · inflow trend — flood trigger at 95%" live>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={DAM_DATA} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <defs>
              <linearGradient id="damGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor(2)} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColor(2)} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="time" tick={axisTick} />
            <YAxis yAxisId="l" tick={axisTick} domain={[75,100]} unit="%" />
            <YAxis yAxisId="r" orientation="right" tick={axisTick} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={legendStyle} />
            <Area yAxisId="l" type="monotone" dataKey="level"     fill="url(#damGrad)" stroke={chartColor(2)} strokeWidth={2.5} name={t("Dam Level %")} />
            <Line  yAxisId="r" type="monotone" dataKey="discharge" stroke={chartColor(7)}    strokeWidth={2} dot={false} name={t("Discharge (cumecs)")} />
            <Line  yAxisId="r" type="monotone" dataKey="inflow"    stroke={chartColor(3)}    strokeWidth={1.5} dot={false} strokeDasharray="4 2" name={t("Inflow (cumecs)")} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 mt-2 text-[9px] flex-wrap">
          <span className="text-blue-700 font-bold">{t("Current")}: 87% · 244 cumecs</span>
          <span className="text-amber-700 font-bold">⚠ {t("Alert Threshold")}: 90%</span>
          <span className="text-red-700 font-bold">🚨 {t("Flood Level")}: 95%</span>
        </div>
      </ChartCard>

      {/* Disaster Scenarios */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-3">{t("Disaster Scenario SOPs — Pre-Modelled")}</h3>
        <div className="space-y-2">
          {DISASTER_SCENARIOS.map((s, i) => {
            const isOpen = expandedScn === s.id;
            return (
              <motion.div key={s.id}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                className={`rounded-2xl border cursor-pointer transition-all ${
                  s.severity === "CRITICAL" ? "border-red-200" : "border-orange-200"
                } ${isOpen ? "shadow-card-hover" : ""}`}
                onClick={() => setExpandedScn(isOpen ? null : s.id)}
              >
                <div className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full border ${
                        s.severity === "CRITICAL" ? "border-red-200 bg-red-100 text-red-700" : "border-orange-200 bg-orange-100 text-orange-700"
                      }`}>{t(s.severity)}</span>
                      <span className="text-[9px] font-bold text-violet-600">{t("AI prob")}: {s.probability}%</span>
                    </div>
                    <p className="text-sm font-black text-slate-900">{t(s.name)}</p>
                    <p className="text-[10px] text-amber-700 mt-0.5">{t("Trigger")}: {t(s.trigger)}</p>
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" /> :
                             <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.22 }}
                      className="overflow-hidden border-t border-slate-200">
                      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white/60 rounded-b-2xl">
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("SOP Steps")}</div>
                          {s.steps.map((step, idx) => (
                            <div key={idx} className="flex items-start gap-1.5 mb-1.5">
                              <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 text-[8px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{idx+1}</span>
                              <span className="text-[10px] text-slate-700">{t(step)}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Resources Required")}</div>
                          {s.resources.map((r, idx) => (
                            <div key={idx} className="flex items-start gap-1.5 mb-1.5">
                              <Shield size={10} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                              <span className="text-[10px] text-slate-700">{t(r)}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Evacuation Route")}</div>
                          <p className="text-[10px] text-blue-700 font-bold bg-blue-50 p-2 rounded-lg">{t(s.evacuationRoute)}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Pre-positioned Resources */}
      <ChartCard title="Pre-positioned Resources — Simhastha 2027"
        subtitle="10 resource types · quantity · location · readiness status">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Resource")}</th>
                <th className="text-center">{t("Quantity")}</th>
                <th className="text-left">{t("Location")}</th>
                <th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {PRE_POSITIONED.map((r, i) => (
                <motion.tr key={r.resource} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
                  <td className="font-bold text-slate-900">{t(r.resource)}</td>
                  <td className="text-center font-black text-blue-700">{r.quantity.toLocaleString()}</td>
                  <td className="text-[10px] text-slate-500">{t(r.location)}</td>
                  <td>
                    <span className={`text-[8px] font-black px-2 py-0.5 rounded-full ${statusClasses(PREPOS_STATUS_BUCKET[r.status] || "neutral")}`}>{t(r.status)}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}
